import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, UserX, CalendarX, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isAwaitingAbn, toastDraftAwaitingAbn } from "../lib/draftToast";
import { COMPLETE_UNDO_MS, undoCompleteAction } from "../lib/undoComplete";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  deferNeedsOutcome,
  fetchNeedsOutcome,
  NEEDS_OUTCOME_CHANGED_EVENT,
  NEEDS_OUTCOME_OPEN_EVENT,
} from "../lib/needsOutcome";

const fmtTime = (iso) =>
  new Date(iso)
    .toLocaleTimeString("en-AU", {
      timeZone: "Australia/Melbourne",
      hour: "numeric",
      minute: "2-digit",
    })
    .toLowerCase();

const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", {
    timeZone: "Australia/Melbourne",
    weekday: "short",
    day: "numeric",
    month: "short",
  });

const firstName = (s) => (s.client_name || "this client").trim().split(" ")[0];
const MAX_ROWS = 5;

export default function NeedsOutcomePrompt() {
  const navigate = useNavigate();
  const { readOnly } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  // The pop-up floats above everything (z-60) so it can be used while a drawer
  // is open. That must never leave it sitting ON TOP of a dialog — it would
  // cover the dialog's buttons. So: while any dialog or sheet is mounted
  // (this pop-up's own "Who cancelled?" included), the pop-up steps aside and
  // comes straight back when the dialog closes.
  const [modalOpen, setModalOpen] = useState(false);
  const loading = useRef(false);
  // Synchronous latch: `busyId` state arrives one render late, so two clicks
  // in the same tick would both fire. The ref is what stops the second.
  const acting = useRef(false);

  const load = useCallback(async (autoOpen) => {
    if (loading.current) return;
    loading.current = true;
    try {
      const list = await fetchNeedsOutcome();
      setRows(list);
      if (autoOpen && list.length > 0) setOpen(true);
      if (list.length === 0) setOpen(false);
    } catch {
      /* never block the app on this */
    } finally {
      loading.current = false;
    }
  }, []);

  // Watch for any Radix dialog / alert dialog / sheet being mounted anywhere.
  useEffect(() => {
    const MODAL_SELECTOR = '[role="dialog"], [role="alertdialog"]';
    const check = () => setModalOpen(!!document.querySelector(MODAL_SELECTOR));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  // App open + every time the tab regains focus.
  useEffect(() => {    load(true);
    const onFocus = () => load(true);
    const onVisible = () => {
      if (document.visibilityState === "visible") load(true);
    };
    const onOpenRequest = () => {
      setOpen(true);
      load(false);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(NEEDS_OUTCOME_OPEN_EVENT, onOpenRequest);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(NEEDS_OUTCOME_OPEN_EVENT, onOpenRequest);
    };
  }, [load]);

  const drop = (id) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.session_id !== id);
      if (next.length === 0) setOpen(false);
      return next;
    });
    window.dispatchEvent(new Event(NEEDS_OUTCOME_CHANGED_EVENT));
  };

  const setStatus = async (s, status, extra = {}) => {
    if (acting.current) return;
    acting.current = true;
    setBusyId(s.session_id);
    try {
      const { data } = await api.put(`/sessions/${s.session_id}/status`, { status, ...extra });
      const inv = data?.auto_invoice;
      if (status === "completed") {
        const undo = {
          duration: COMPLETE_UNDO_MS,
          action: undoCompleteAction(s.session_id, async () => {
            window.dispatchEvent(new Event(NEEDS_OUTCOME_CHANGED_EVENT));
            load(false);
          }),
        };
        if (inv) {
          const total = `AUD $${Number(inv.total).toFixed(2)}`;
          toast.success(`Invoice ${inv.invoice_number} (${total}) created`, {
            ...undo,
            description: isAwaitingAbn(inv)
              ? "Saved as a draft until your ABN is added — nothing emailed."
              : s.client_email
                ? `Sending to ${firstName(s)} shortly — undo now and no email goes out.`
                : `${firstName(s)} has no email on file, so nothing will be sent.`,
          });
          if (isAwaitingAbn(inv)) toastDraftAwaitingAbn(navigate);
        } else {
          toast.success(`${firstName(s)}'s session completed`, undo);
        }
      } else if (status === "no-show") {
        toast.success(`${firstName(s)} marked as a no-show`, {
          duration: 10000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await api.post(`/sessions/${s.session_id}/restore`);
                toast.success("Back to scheduled — no-show removed");
                window.dispatchEvent(new Event(NEEDS_OUTCOME_CHANGED_EVENT));
                load(false);
              } catch (err) {
                toast.error(apiError(err, "Couldn't undo — please try again"));
              }
            },
          },
        });
      } else {
        toast.success(
          `Cancelled — logged as ${extra.cancelled_by === "client" ? "client" : "you"} cancelled`,
          {
            duration: 10000,
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  const { data } = await api.post(`/sessions/${s.session_id}/restore`);
                  toast.success(
                    data?.pack_coverage_dropped
                      ? "Restored. No pack credit was available, so this class will be invoiced when it is completed."
                      : "Cancellation undone — session restored",
                  );
                  window.dispatchEvent(new Event(NEEDS_OUTCOME_CHANGED_EVENT));
                  load(false);
                } catch (err) {
                  toast.error(apiError(err, "Couldn't undo — please try again"));
                }
              },
            },
          },
        );
      }
      drop(s.session_id);
    } catch (e) {
      toast.error(apiError(e, "Could not update that session"));
    } finally {
      acting.current = false;
      setBusyId(null);
    }
  };

  const later = (s) => {
    deferNeedsOutcome(s.session_id);
    drop(s.session_id);
  };

  // In read-only mode nothing can be marked, so the whole prompt is hidden.
  if (readOnly || !open || rows.length === 0) return null;
  const shown = rows.slice(0, MAX_ROWS);
  const hidden = rows.length - shown.length;

  return (
    <>
      {!modalOpen && (
      <div
        data-testid="needs-outcome-prompt"
        // This pop-up floats above an open drawer or dialog, so it has to opt
        // back into pointer events (a Radix modal sets `pointer-events: none`
        // on <body>) — see index.css — and swallow the tap so the modal
        // underneath doesn't treat it as an outside click and close.
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        className="fixed z-[60] bottom-20 md:bottom-6 right-3 left-3 sm:left-auto sm:right-6 sm:w-[340px] rounded-lg border border-[#ECDCA6] bg-[#FFFFFF]/95 backdrop-blur shadow-2xl animate-in slide-in-from-bottom-4 duration-300"
      >
        <div className="flex items-start gap-2 px-3 py-2.5 border-b border-[#E8E1D6]">
          <Clock className="w-4 h-4 text-[#7A5E18] shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-widest font-bold text-[#7A5E18]">
              Needs outcome ({rows.length})
            </p>
            <p className="text-[11px] text-[#6B635C] leading-snug mt-0.5">
              These classes have finished. Nothing is invoiced until you mark them.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="needs-outcome-close"
            aria-label="Close"
            className="text-[#6B635C] hover:text-[#2A2623] shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <ul className="divide-y divide-[#E8E1D6] max-h-[52vh] overflow-y-auto">
          {shown.map((s) => {
            // Every row in this queue has finished, but the guard is explicit:
            // No-show never shows for a class that hasn't started.
            const started = Date.now() >= new Date(s.starts_at).getTime();
            return (
            <li key={s.session_id} className="px-3 py-2.5" data-testid={`needs-outcome-row-${s.session_id}`}>
              <p className="text-xs text-[#2A2623] leading-snug">
                Did you complete{" "}
                <span className="font-semibold">{firstName(s)}'s</span> session at{" "}
                <span className="font-semibold">{fmtTime(s.starts_at)}</span>?
              </p>
              <p className="text-[10px] uppercase tracking-widest text-[#6B635C] mt-0.5">
                {fmtDay(s.starts_at)}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {started && (
                  <Button
                    size="sm"
                    disabled={!!busyId}
                    onClick={() => setStatus(s, "completed")}
                    data-testid={`needs-outcome-complete-${s.session_id}`}
                    className="h-7 px-2 text-[11px] bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0F6B6B]/90"
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Complete
                  </Button>
                )}
                {started && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!!busyId}
                    onClick={() => setStatus(s, "no-show")}
                    data-testid={`needs-outcome-noshow-${s.session_id}`}
                    className="h-7 px-2 text-[11px] border-[#ECDCA6] text-[#7A5E18] hover:bg-[#FBF4DD] bg-transparent"
                  >
                    <UserX className="w-3 h-3 mr-1" /> No-show
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!!busyId}
                  onClick={() => setCancelFor(s)}
                  data-testid={`needs-outcome-cancel-${s.session_id}`}
                  className="h-7 px-2 text-[11px] border-[#E8E1D6] text-[#2A2623] hover:bg-[#FAF7F2] bg-transparent"
                >
                  <CalendarX className="w-3 h-3 mr-1" /> Cancel
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!!busyId}
                  onClick={() => later(s)}
                  data-testid={`needs-outcome-later-${s.session_id}`}
                  className="h-7 px-2 text-[11px] text-[#6B635C] hover:text-[#2A2623]"
                >
                  Later
                </Button>
              </div>
            </li>
            );
          })}
        </ul>
        {hidden > 0 && (
          <p
            className="text-[10px] text-[#6B635C] px-3 py-1.5 border-t border-[#E8E1D6]"
            data-testid="needs-outcome-more"
          >
            +{hidden} more waiting on the Schedule
          </p>
        )}
      </div>
      )}

      <AlertDialog open={!!cancelFor} onOpenChange={(v) => !v && setCancelFor(null)}>
        <AlertDialogContent
          data-testid="needs-outcome-who-cancelled"
          className="bg-[#FFFFFF] border tm-divider text-[#2A2623]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">
              Who cancelled?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C] text-sm">
              Logged for reporting only — it never changes {cancelFor ? firstName(cancelFor) : "the client"}'s
              reliability score.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Same order and labels as the Session Details version: the two
              choices first, then Keep session at the bottom. */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              data-testid="needs-outcome-cancel-by-client"
              onClick={() => {
                const s = cancelFor;
                setCancelFor(null);
                setStatus(s, "cancelled", { cancelled_by: "client" });
              }}
              className="h-11 bg-[#FBF4DD] text-[#7A5E18] hover:bg-[#F6ECC9] border border-[#ECDCA6]"
            >
              Client cancelled
            </Button>
            <Button
              data-testid="needs-outcome-cancel-by-trainer"
              onClick={() => {
                const s = cancelFor;
                setCancelFor(null);
                setStatus(s, "cancelled", { cancelled_by: "trainer" });
              }}
              className="h-11 bg-[#E1EDF7] text-[#1F5C8F] hover:bg-[#D3E4F4] border border-[#B9D4EC]"
            >
              I cancelled
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="needs-outcome-who-dismiss"
              className="h-11 border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Keep session
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
